import { Box, Button, Checkbox, FormControl, Input, Option, Select, SvgIcon, Tab, TabList, TabPanel, Table, Tabs, Typography } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import FollowUpDialog from "../Dialogs/FollowUpDialog";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ProposalTracking from "./ProposalTracking";
import { SxProps } from "@mui/joy/styles/types";

export interface FollowUp {
	BuildingName: string;
	FollowUpID: string;
	Address: string;
	Contact1Name: string;
	Contact1Email: string;
	Contact1Phone: string;
	Contact2Name: string;
	Contact2Email: string;
	Contact2Phone: string;
	Contact3Name: string;
	Contact3Email: string;
	Contact3Phone: string;
	DateAssessed?: Date;
	DateContract?: Date;
	DateLastUpdated: Date;
	FollowUps: string;
	Landings: string;
	Lost: boolean;
	Won: boolean;
	Maintenance: boolean;
	Modern: boolean;
	Repair: boolean;
	Notes: string;
	LastNote: string;
}
// Main FollowUps component with tabs for Follow Ups and Proposal Tracking
export default function FollowUps() {
	const [tabIdx, setTabIdx] = React.useState(0);
	const tabSx = { backgroundColor: "initial !important" } as SxProps;

	return (
		<Tabs value={tabIdx} onChange={(event, value) => setTabIdx(value as number)}>
			<TabList>
				<Tab color={tabIdx === 0 ? "success" : "neutral"} sx={tabSx}>
					Follow Ups
				</Tab>
				<Tab color={tabIdx === 1 ? "success" : "neutral"} sx={tabSx}>
					Proposal Tracking
				</Tab>
			</TabList>
			<TabPanel value={0}>
				<FollowUpsContent />
			</TabPanel>
			<TabPanel value={1}>
				<ProposalTracking />
			</TabPanel>
		</Tabs>
	);
}

// Follow Ups content - extracted from original component
function FollowUpsContent() {
	const [data, setData] = React.useState<Array<FollowUp>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [order, setOrder] = React.useState(true);
	const [orderBy, setOrderBy] = React.useState(1);

	const [modal, setModal] = React.useState(false);
	const [modalFup, setModalFup] = React.useState<FollowUp>({
		FollowUpID: "",
		BuildingName: "",
		Address: "",
		Lost: false,
		Won: false,
		Maintenance: false,
		Modern: false,
		Repair: false,
		FollowUps: "",
		Landings: "",
		Notes: "",
		DateAssessed: new Date(),
		DateContract: new Date(),
		Contact1Name: "",
		Contact1Phone: "",
		Contact1Email: "",
		Contact2Name: "",
		Contact2Phone: "",
		Contact2Email: "",
		Contact3Name: "",
		Contact3Phone: "",
		Contact3Email: "",

	} as FollowUp);
	const [mode, setMode] = React.useState(0);
	React.useEffect(() => {
		getData();
	}, [mode]);
	const getData = () => {
		fetch("Inspection/GetFollowUps", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify(mode),
		})
			.then((x) => x.json())
			.then((x) => {
				const res = x.map((y: any) => {
					const splitNotes = y.follow_ups && y.follow_ups.split("\n");
					return {
						BuildingName: y.building_name,
						Address: y.address,
						Contact1Email: y.contact_1_email,
						Contact1Name: y.contact_1_name,
						Contact1Phone: y.contact_1_phone,
						Contact2Email: y.contact_2_email,
						Contact2Name: y.contact_2_name,
						Contact2Phone: y.contact_2_phone,
						Contact3Email: y.contact_3_email,
						Contact3Name: y.contact_3_name,
						Contact3Phone: y.contact_3_phone,
						DateAssessed: Util.ParseDate(y.date_assessed),
						DateContract: Util.ParseDate(y.date_contract),
						DateLastUpdated: Util.ParseDate(y.date_last_updated),
						FollowUpID: y.follow_up_id,
						FollowUps: y.follow_ups,
						Landings: y.landings,
						Lost: y.lost,
						Won: y.won,
						Maintenance: y.maint,
						Modern: y.modern,
						Notes: y.notes,
						Repair: y.repair,
						LastNote: splitNotes && splitNotes[splitNotes.length - 1],
					} as FollowUp;
				});
				setData(res);
			});
	};

	const markWon = (followUpId: string, event: React.MouseEvent) => {
		event.stopPropagation();
		fetch("Inspection/MarkFollowUpWon", {
			method: "Post",
			headers: {
				"Content-Type": "application/json",
			},
			cache: "no-cache",
			body: JSON.stringify(followUpId),
		}).then(() => getData());
	};

	const dt = (data ?? []).filter((x) => Util.isNullOrEmpty(globalFilter) || x.BuildingName.toLowerCase().includes(globalFilter.toLowerCase()) || x.FollowUps?.toLowerCase()?.includes(globalFilter.toLowerCase()));
	const maxPages = Math.ceil(dt.length / 25);
	const cols = mode === 0 ? ["Building", "Date Attended", "Date of Contract", "Follow up notes", ""] : ["Building", "Date Attended", "Date of Contract", "Follow up notes"];
	const old = new Date("1/1/1900");
	dt.sort((a, b) => {
		switch (cols[orderBy]) {
			case "Building":
				return !order ? a.BuildingName.localeCompare(b.BuildingName) : b.BuildingName.localeCompare(a.BuildingName);
			case "Date Attended":
				const ad = (a.DateAssessed ?? old).getTime();
				const bd = (b.DateAssessed ?? old).getTime();
				if ((ad > bd && !order) || (ad < bd && order)) return 1;
				if (ad === bd) return 0;
				return -1;
			case "Date of Contract":
				const aad = a.DateContract ?? old;
				const bbd = a.DateContract ?? old;
				if ((aad > bbd && !order) || (aad < bbd && order)) return 1;
				if (aad === bbd) return 0;
				return -1;
			case "Follow up notes":
				return !order ? a.FollowUps.localeCompare(b.FollowUps) : b.FollowUps.localeCompare(a.FollowUps);
		}
		return 0;
	});
	const thisData = dt.slice(pageNumber * 25, (pageNumber + 1) * 25);
	return (
		<>
			<FollowUpDialog
				open={modal}
				followUp={modalFup}
				close={() => {
					setModal(false);
					setModalFup({
						FollowUpID: "",
						BuildingName: "",
						Address: "",
						Lost: false,
						Won: false,
						Maintenance: false,
						Modern: false,
						Repair: false,
						FollowUps: "",
						Landings: "",
						Notes: "",
						DateAssessed: new Date(),
						DateContract: new Date(),
						Contact1Name: "",
						Contact1Phone: "",
						Contact1Email: "",
						Contact2Name: "",
						Contact2Phone: "",
						Contact2Email: "",
						Contact3Name: "",
						Contact3Phone: "",
						Contact3Email: "",

					} as FollowUp);
					getData();
				}}
			/>
			<Box sx={{ overflowY: "auto", padding: 2 }}>
				<div>
					<Typography level="title-lg">Follow Ups</Typography>
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<Select defaultValue={mode} sx={{ mr: 1 }} onChange={(event, value) => setMode(value as number)}>
							<Option value={0}>Pending</Option>
							<Option value={1}>Lost</Option>
							<Option value={3}>Won</Option>
							<Option value={2}>All Buildings</Option>
						</Select>
						<Button
							onClick={() => {
								setModal(true);
							}}>
							Add Follow Up
						</Button>

						<FormControl sx={{ marginLeft: 1 }}>
							<Input
								name="Search"
								placeholder="Global Search"
								onChange={(event) => {
									setGlobalFilter(event.target.value);
								}}
								value={globalFilter}
							/>
						</FormControl>
					</Box>
				</div>
				<Table stripe={"odd"} hoverRow>
					<thead>
						<tr>
							{cols.map((x, idx) => {
								return (
									<th
										key={idx}
										style={{ cursor: "pointer" }}
										onClick={() => {
											setOrderBy(idx);
											setOrder(orderBy === idx ? !order : true);
										}}>
										{x}
										{orderBy === idx ? <SvgIcon onClick={() => setOrder(!order)} component={order ? ArrowDownwardIcon : ArrowUpwardIcon} sx={{ cursor: "pointer", marginTop: 1 }} /> : <></>}
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{thisData?.map((x) => {
							return (
								<tr
									key={x.FollowUpID}
									onClick={() => {
										setModal(true);
										setModalFup(x);
									}}
									style={{ cursor: "pointer" }}>
									<td>{x.BuildingName}</td>
									<td>{Util.Formatter(x.DateAssessed, "D")}</td>
									<td>{Util.Formatter(x.DateContract, "D")}</td>
									<td>{x.FollowUps}</td>
									{mode === 0 && (
										<td>
											<Button
												size="sm"
												color="success"
												onClick={(event) => markWon(x.FollowUpID, event)}>
												Won
											</Button>
										</td>
									)}
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr>
							<td colSpan={mode === 0 ? 5 : 4}>
								<div style={{ float: "right" }}>
									<span>
										<KeyboardArrowLeftIcon onClick={() => pageNumber > 0 && setPageNumber(pageNumber - 1)} sx={{ verticalAlign: "middle", cursor: "pointer" }} /> {`Page ${pageNumber + 1} of ${maxPages}`} <KeyboardArrowRightIcon onClick={() => maxPages > pageNumber + 1 && setPageNumber(pageNumber + 1)} sx={{ verticalAlign: "middle", cursor: "pointer" }} />
									</span>
								</div>
							</td>
						</tr>
					</tfoot>
				</Table>
			</Box>
		</>
	);
}
